import {
  ActivityNames,
  CaseReport,
  CaseReportTotals
} from './CaseTypes';
import { formatTimeDisplay, getActivityLabel, getActivityColor } from '../utils/caseUtils';
import { StepData } from '../components/Charts/CaseStepBreakdownChart';

/** Definition for a step in the breakdown chart */
interface StepDefinition {
  activity: ActivityNames;
  fieldKey: string;
  /** Optional condition - step is included only if this returns true (or is omitted) */
  condition?: (totals: TotalsExtended) => boolean;
}

/** Extended totals type - CaseReportTotals already includes optional split fields */
type TotalsExtended = CaseReportTotals;

/**
 * Adapts CaseReport data into StepData[] for the CaseStepBreakdownChart component.
 * Supports both combined and split data formats for backward compatibility.
 */
export default class StepBreakdownAdapter {
  caseReport: CaseReport;

  constructor(caseReport: CaseReport) {
    this.caseReport = caseReport;
  }

  /**
   * Step definitions in display order. Each step specifies:
   * - activity: The ActivityNames enum value (for label and color)
   * - fieldKey: The key in totals to get the time value
   * - condition: Optional function to determine if step should be included
   */
  private static readonly STEP_DEFINITIONS: StepDefinition[] = [
    // 1. Exposure
    { activity: ActivityNames.EXPOSURE, fieldKey: 'exposure_time' },

    // 2. Anatomy Localization (split - only when split data available)
    {
      activity: ActivityNames.ANATOMY_LOCALIZATION,
      fieldKey: 'anatomy_localization_time',
      condition: (totals) => StepBreakdownAdapter.hasSplitCannulationData(totals)
    },

    // 3. Cannulation (split - only when split data available)
    {
      activity: ActivityNames.CANNULATION,
      fieldKey: 'cannulation_time',
      condition: (totals) => StepBreakdownAdapter.hasSplitCannulationData(totals)
    },

    // 4. Screw Placement (split - only when split data available)
    {
      activity: ActivityNames.SCREW_PLACEMENT,
      fieldKey: 'screw_placement_time',
      condition: (totals) => StepBreakdownAdapter.hasSplitCannulationData(totals)
    },

    // 5. Cannulation & Screw Placement (combined - only when split data NOT available)
    {
      activity: ActivityNames.CANNULATION_SCREW_PLACEMENT,
      fieldKey: 'total_cannulation_screw_placement_time',
      condition: (totals) => !StepBreakdownAdapter.hasSplitCannulationData(totals)
    },

    // 6. Decompression/Alignment
    { activity: ActivityNames.DECOMPRESSION_ALIGNMENT, fieldKey: 'alignment_decompression_time' },

    // 7. Closure
    { activity: ActivityNames.CLOSURE, fieldKey: 'closure_time' },

    // 8. Post Cannulation Activity (only when no IOM session)
    {
      activity: ActivityNames.POST_CANNULATION,
      fieldKey: 'post_cannulation_activity',
      condition: (totals) => !totals.total_iom_time || totals.total_iom_time === 0
    }
  ];

  /** Check if split cannulation data is available (set by CaseReportAdapter) */
  private static hasSplitCannulationData(totals: TotalsExtended): boolean {
    return totals.has_split_cannulation_data === true;
  }

  /** Check if a field exists (including 0 values) */
  private static fieldExists(value: number | undefined | null): boolean {
    return value !== undefined && value !== null;
  }

  /**
   * Get step breakdown data for the CaseStepBreakdownChart component.
   * Supports both combined and split data formats for backward compatibility.
   *
   * Series are always returned in this specific order (when available):
   * 1. Exposure
   * 2. Anatomy Localization (split)
   * 3. Cannulation (split, from Cannulation & Screw Placement)
   * 4. Screw Placement (split, from Cannulation & Screw Placement)
   * 5. Cannulation & Screw Placement (combined, when split fields not available)
   * 6. Decompression/Alignment
   * 7. Closure
   * 8. Post Cannulation Activity (conditional on IOM session)
   *
   * Note: A value of 0 IS included if the field exists.
   * Only when a field is not provided at all is it omitted.
   */
  getStepBreakdownData = (): StepData[] => {
    if (!this.caseReport.totals) {
      return [];
    }

    const totals = this.caseReport.totals as TotalsExtended;

    return StepBreakdownAdapter.STEP_DEFINITIONS
      .filter(step => {
        const time = totals[step.fieldKey as keyof TotalsExtended] as number | undefined;
        const exists = StepBreakdownAdapter.fieldExists(time);
        const conditionMet = !step.condition || step.condition(totals);
        return exists && conditionMet;
      })
      .map(step => {
        const time = totals[step.fieldKey as keyof TotalsExtended] as number;
        return {
          name: getActivityLabel(step.activity),
          time,
          displayTime: formatTimeDisplay(time),
          color: getActivityColor(step.activity)
        };
      });
  };

  /**
   * Format the total step time for display in the chart header.
   * Returns the captured operative time in a human-readable format.
   */
  formatTotalStepTime = (): string => {
    if (!this.caseReport.totals) {
      return '0 mins';
    }
    const capturedTime = this.caseReport.totals.captured_operative_time;
    if (capturedTime === undefined || capturedTime === null) {
      return '0 mins';
    }
    return formatTimeDisplay(capturedTime);
  };

  /**
   * Calculate and return the total step time from step breakdown data.
   * This sums all visible steps rather than using captured_operative_time.
   */
  calculateTotalStepTime = (): string => {
    const steps = this.getStepBreakdownData();
    const totalMinutes = steps.reduce((sum, step) => sum + step.time, 0);
    return formatTimeDisplay(totalMinutes, true);
  };
}
